import { useEffect, useState } from "react";
import { Button, Container, Grid, MenuItem, TextField } from "@mui/material";
import { useQuery } from "react-query";
import { DatePicker, LoadingButton } from "@mui/lab";
import HeaderBreadcrumbs from "src/components/HeaderBreadcrumbs";
import Page from "src/components/Page";
import useSettings from "src/hooks/useSettings";
import { PATH_DASHBOARD } from "src/routes/paths";
import { getSchoolfilter } from "../api";
import { useSnackbar } from "notistack";
import axiosInstance from "src/utils/axios";
import { useForm, FormProvider } from "react-hook-form";
import RHFAutocomplete from "src/components/hook-form/RHFAutocomplete";

export default function GSTReport({ type }) {
  const { themeStretch } = useSettings();
  const { enqueueSnackbar } = useSnackbar();
  const [schoolList, setSchoolList] = useState([]);
  const schoolID = JSON.parse(sessionStorage.getItem("schoolId"));
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [loadingLabel, setLoadingLabel] = useState(null);
  const methods = useForm({
    defaultValues: {
      school: null,
    },
  });

  const { setValue, watch } = methods;

  const selectedSchool = watch("school")?.branchID || "";

  useEffect(() => {
    if (schoolID) {
      getSchoolfilter(schoolID).then((schools) => {
        setSchoolList(schools || []);
        if (schools.length === 1) {
          setValue("school", schools[0]);
        }
      });
    }
  }, [schoolID, setValue]);

  const fetchdata = async () => {
    if (selectedSchool && fromDate && toDate) {
      try {
        setLoadingLabel(true);
        let reqUrl = "/report/school/salesWithOutGst";
        let outputFilename = `without-gst-${new Date()}.xlsx`;
        if (type === "withGst") {
          reqUrl = "report/school/salesWithGst";
          outputFilename = `with-gst-${new Date()}.xlsx`;
        }
        if (type === "shipping") {
          reqUrl = "report/school/salesShipping";
          outputFilename = `shipping-${new Date()}.xlsx`;
        }

        const config = {
          method: "GET",
          url: `${reqUrl}?schoolID=${selectedSchool}&initialDate=${
            new Date(fromDate).toISOString().split("T")[0]
          }&endDate=${new Date(toDate).toISOString().split("T")[0]}`,

          responseType: "blob",
        };
        const { data } = await axiosInstance(config);
        const url = URL.createObjectURL(data);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", outputFilename);
        document.body.appendChild(link);
        link.click();
        link.remove();
      } catch (error) {
        enqueueSnackbar("No Data found", { variant: "error" });
      } finally {
        setLoadingLabel(false);
      }
    } else {
      enqueueSnackbar("Please select all fields", { variant: "error" });
    }
  };

  useEffect(() => {
    setToDate(null);
    setFromDate(null);
    setValue("school", null);
  }, [type]);
  return (
    <Page title="Report">
      <Container maxWidth={themeStretch ? false : "xl"}>
        <HeaderBreadcrumbs
          heading={`${
            type === "withGst"
              ? "With GST"
              : type === "shipping"
              ? "Shipping"
              : "Without GST"
          }`}
          links={[
            { name: "Dashboard", href: PATH_DASHBOARD.root },
            { name: "Report" },
            {
              name: `
                ${
                  type === "withGst"
                    ? "With GST"
                    : type === "shipping"
                    ? "Shipping"
                    : "Without GST"
                }`,
            },
          ]}
        />
        <FormProvider {...methods}>
          <Grid container spacing={3} mb={2}>
            <Grid item xs={12} sm={12}>
              <RHFAutocomplete
                name="school"
                label="School"
                options={schoolList}
                getOptionLabel={(option) => option?.branchName || ""}
                isOptionEqualToValue={(option, value) =>
                  option?.branchID === value?.branchID
                }
                onChange={(e, value) => {
                  setValue("school", value);
                  setFromDate(null);
                  setToDate(null);
                }}
                disabled={schoolList.length === 0}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <DatePicker
                label="Order From"
                value={fromDate}
                clearable
                disabled={!selectedSchool}
                onChange={(value) => {
                  setFromDate(value);
                  setToDate(null);
                }}
                disableFuture
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    size="small"
                    onKeyDown={(e) => {
                      e.preventDefault();
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <DatePicker
                label="Order to"
                value={toDate}
                clearable
                onChange={(value) => {
                  setToDate(value);
                }}
                disableFuture
                disabled={!fromDate}
                minDate={fromDate}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    size="small"
                    onKeyDown={(e) => {
                      e.preventDefault();
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item>
              <LoadingButton
                variant="contained"
                color="primary"
                loading={loadingLabel}
                onClick={() => {
                  fetchdata();
                }}
              >
                Load Data
              </LoadingButton>
            </Grid>
          </Grid>
        </FormProvider>
      </Container>
    </Page>
  );
}
